import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { webRTCService } from '../services/webrtc';
import { useAuth } from '../hooks/useAuth';
import { socketService } from '../services/socket';
import { VoiceRoom, Participant, ChatMessage } from '../components/VoiceComponents';
import { Gift } from '../components/StreamGifts';
import { getImageUrl } from '../services/api';

interface VoiceChatContextType {
    rooms: VoiceRoom[];
    selectedRoom: VoiceRoom | null;
    isInRoom: boolean;
    isMuted: boolean;
    isSpeaking: boolean;
    volume: number;
    chatMessages: ChatMessage[];
    userBalance: number; // Wallet
    isMinimized: boolean; // Mini player state

    // Actions
    joinRoom: (room: VoiceRoom) => void;
    leaveRoom: () => void;
    createRoom: (title: string, tags?: string[]) => void;
    requestToSpeak: () => void;
    acceptSpeakerRequest: (participantId: string) => void;
    rejectSpeakerRequest: (participantId: string) => void;
    toggleMute: () => void;
    toggleMinimize: () => void;
    sendMessage: (text: string) => void;
    sendGift: (gift: Gift, recipientId?: string) => void;
    removeSpeaker: (participantId: string) => void;

    // State Setters (if needed strictly, but actions preferred)
    setIsChatVisible: (visible: boolean) => void;
    isChatVisible: boolean;

    // Video
    isVideoEnabled: boolean;
    videoStream: MediaStream | null;
    startVideo: () => Promise<void>;
    stopVideo: () => void;
    videoRef: React.RefObject<HTMLVideoElement>;
}

const VoiceChatContext = createContext<VoiceChatContextType | undefined>(undefined);

export const VoiceChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, isVipActive } = useAuth();

    // --- State ---
    const [rooms, setRooms] = useState<VoiceRoom[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<VoiceRoom | null>(null);
    const [isInRoom, setIsInRoom] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [volume, setVolume] = useState(50);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [userBalance, setUserBalance] = useState(1500);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(true);

    // Video State
    const [isVideoEnabled, setIsVideoEnabled] = useState(false);
    const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // --- Initialization & Simulation ---

    // Load Rooms via Socket
    useEffect(() => {
        const socket = socketService.getSocket();
        if (socket) {
            if (!socket.connected) socket.connect();
            
            socket.on('voiceRoomsSync', (syncRooms: VoiceRoom[]) => {
                setRooms(syncRooms);
                setSelectedRoom(prev => {
                    if (!prev) return null;
                    const updated = syncRooms.find(r => r.id === prev.id);
                    return updated || null; // If room is deleted, clear it
                });
            });
            
            // Ask for an initial sync if possible, or wait for the emit from connection
            return () => {
                socket.off('voiceRoomsSync');
            };
        }
    }, []);

    // Listen to Voice Room Chat Messages
    useEffect(() => {
        const socket = socketService.getSocket();
        if (socket && selectedRoom) {
            const handleRoomMessage = (data: { roomId: string, message: ChatMessage }) => {
                if (data.roomId === selectedRoom.id) {
                    setChatMessages(prev => {
                        // Prevent duplicates
                        if (prev.some(m => m.id === data.message.id)) return prev;
                        return [...prev, data.message];
                    });
                }
            };
            socket.on('voiceRoomMessage', handleRoomMessage);
            return () => {
                socket.off('voiceRoomMessage', handleRoomMessage);
            };
        }
    }, [selectedRoom?.id]);

    // Active Room Simulation (Participants speaking, audience changing)
    useEffect(() => {
        if (!selectedRoom) return;

        const voiceInterval = setInterval(() => {
            setRooms(prev => prev.map(room =>
                room.id === selectedRoom.id
                    ? {
                        ...room,
                        participants: room.participants.map(p => ({
                            ...p,
                            isSpeaking: Math.random() > 0.7 && !p.isMuted
                        })),
                        audienceCount: Math.max(0, room.audienceCount + (Math.random() > 0.5 ? 1 : -1))
                    }
                    : room
            ));

            // Update selected room state to match (for UI consistency)
            setSelectedRoom(prev => {
                if (!prev) return null;
                // Find updated room in prev rooms list logic would be recursive/complex with setters clash
                // Simplified: We accept that selectedRoom might lag slightly behind 'rooms' list state 
                // OR we sync them. Let's rely on 'rooms' for list and 'selectedRoom' for session.
                // Better: Update selectedRoom based on the calculation too.
                return prev;
            });

        }, 3000);

        return () => clearInterval(voiceInterval);
    }, [selectedRoom?.id]); 

    // Handle room getting deleted globally
    useEffect(() => {
        if (!selectedRoom && isInRoom) {
            setIsInRoom(false);
            setIsMinimized(false);
            setIsMuted(false);
            setIsSpeaking(false);
            setChatMessages([]);
            stopVideo();
            webRTCService.leaveRoom();
        }
    }, [selectedRoom, isInRoom]);

    // Sync selectedRoom with rooms list changes (critical for real-time updates)
    useEffect(() => {
        if (!selectedRoom) return;
        const currentUserId = user?.id || (user as any)?._id || 'current-user';
        const isSpeaker = selectedRoom.participants.some(p => p.id === currentUserId);
        
        if (isSpeaker && !webRTCService.hasLocalAudioTrack()) {
            navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
                webRTCService.setLocalStream(stream);
                webRTCService.publishLocalAudio();
            }).catch(err => {
                console.error("Mikrofon izni alınamadı", err);
            });
        } else if (!isSpeaker && webRTCService.hasLocalAudioTrack()) {
            webRTCService.unpublishLocalAudio();
        }
    }, [selectedRoom, user]);

    useEffect(() => {
        if (selectedRoom) {
            const updated = rooms.find(r => r.id === selectedRoom.id);
            if (updated && updated !== selectedRoom) {
                setSelectedRoom(updated);
            }
        }
    }, [rooms]);


    // --- Actions ---

    const joinRoom = (room: VoiceRoom) => {
        // Join as AUDIENCE by default
        const updatedRoom = { ...room, audienceCount: room.audienceCount + 1 };
        const socket = socketService.getSocket();
        if (socket) socket.emit('updateVoiceRoom', updatedRoom);
        
        setSelectedRoom(updatedRoom);
        setIsInRoom(true);
        setIsMinimized(false); // Maximize on join

        if (user?.id) webRTCService.joinRoom(room.id, user.id);
    };

    const leaveRoom = () => {
        if (selectedRoom) {
            const currentUserId = user?.id || (user as any)?._id || 'current-user';
            const isSpeaker = selectedRoom.participants.some(p => p.id === currentUserId);
            const isHost = selectedRoom.participants.some(p => p.id === currentUserId && p.isHost) || selectedRoom.host === (user?.username || 'Sen');
            
            const socket = socketService.getSocket();
            if (socket) {
                if (isHost) {
                    setRooms(prev => prev.filter(r => r.id !== selectedRoom.id));
                    socket.emit('deleteVoiceRoom', selectedRoom.id);
                } else {
                    const updatedRoom = {
                        ...selectedRoom,
                        participants: isSpeaker ? selectedRoom.participants.filter(p => p.id !== currentUserId) : selectedRoom.participants,
                        audienceCount: isSpeaker ? selectedRoom.audienceCount : Math.max(0, selectedRoom.audienceCount - 1)
                    };
                    socket.emit('updateVoiceRoom', updatedRoom);
                }
            }
        }
        setSelectedRoom(null);
        setIsInRoom(false);
        setIsMinimized(false);
        setIsMuted(false);
        setIsSpeaking(false);
        setChatMessages([]);
        stopVideo();
        webRTCService.leaveRoom();
    };

    const createRoom = async (title: string, tags: string[] = []) => {
        if (!title.trim()) return;

        const currentUser: Participant = {
            id: user?.id || (user as any)?._id || 'current-user',
            username: user?.username || 'Sen',
            avatar: user?.username ? user.username[0].toUpperCase() : '🎭',
            profileImage: user?.profileImage ? getImageUrl(user.profileImage) : `https://ui-avatars.com/api/?name=${user?.username || 'Sen'}&background=random`,
            isHost: true,
            isVip: isVipActive ? isVipActive() : false, // Use VIP status
            isSpeaking: false,
            isMuted: false,
            volume: 50,
            gifts: []
        };
        const newRoom: VoiceRoom = {
            id: Date.now().toString(),
            title: title,
            host: user?.username || 'Sen',
            hostAvatar: user?.profileImage ? getImageUrl(user.profileImage) : (user?.username ? user.username[0].toUpperCase() : '🎭'),
            participants: [currentUser], // Host is speaker
            audienceCount: 0,
            maxParticipants: 10,
            isLive: true,
            tags: tags.length > 0 ? tags : ['Yeni']
        };

        const socket = socketService.getSocket();
        if (socket) socket.emit('createVoiceRoom', newRoom);

        setSelectedRoom(newRoom);
        setIsInRoom(true);
        setIsMinimized(false);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            webRTCService.setLocalStream(stream);
        } catch (err) {
            console.error("Mikrofon izni alınamadı", err);
        }

        if (user?.id) webRTCService.joinRoom(newRoom.id, user.id);
    };

    const requestToSpeak = () => {
        if (!selectedRoom) return;
        const currentUserId = user?.id || (user as any)?._id || 'current-user';
        const socket = socketService.getSocket();
        
        if (socket) {
            const requester: Participant = {
                id: currentUserId,
                username: user?.username || 'Sen',
                avatar: user?.username ? user.username[0].toUpperCase() : '🎭',
                profileImage: user?.profileImage ? getImageUrl(user.profileImage) : `https://ui-avatars.com/api/?name=${user?.username || 'Sen'}&background=random`,
                isHost: false,
                isMuted: true,
                isSpeaking: false,
                volume: 0
            };
            
            const updatedRoom = {
                ...selectedRoom,
                speakerRequests: [...(selectedRoom.speakerRequests || []).filter(p => p.id !== currentUserId), requester]
            };
            setSelectedRoom(updatedRoom);
            setRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
            socket.emit('updateVoiceRoom', updatedRoom);
        }
    };

    const acceptSpeakerRequest = (participantId: string) => {
        if (!selectedRoom) return;
        const socket = socketService.getSocket();
        
        if (socket) {
            const requester = (selectedRoom.speakerRequests || []).find(p => p.id === participantId);
            if (requester) {
                const updatedRoom = {
                    ...selectedRoom,
                    participants: [...selectedRoom.participants, requester],
                    speakerRequests: (selectedRoom.speakerRequests || []).filter(p => p.id !== participantId)
                };
                setSelectedRoom(updatedRoom);
                setRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
                socket.emit('updateVoiceRoom', updatedRoom);
            }
        }
    };

    const rejectSpeakerRequest = (participantId: string) => {
        if (!selectedRoom) return;
        const socket = socketService.getSocket();
        
        if (socket) {
            const updatedRoom = {
                ...selectedRoom,
                speakerRequests: (selectedRoom.speakerRequests || []).filter(p => p.id !== participantId)
            };
            setSelectedRoom(updatedRoom);
            setRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
            socket.emit('updateVoiceRoom', updatedRoom);
        }
    };

    const toggleMute = () => {
        const newMuteState = !isMuted;
        setIsMuted(newMuteState);
        webRTCService.toggleAudio(newMuteState);
        
        if (selectedRoom) {
            const currentUserId = user?.id || (user as any)?._id || 'current-user';
            const updatedRoom = {
                ...selectedRoom,
                participants: selectedRoom.participants.map(p => 
                    p.id === currentUserId ? { ...p, isMuted: newMuteState } : p
                )
            };
            setSelectedRoom(updatedRoom);
            setRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
            
            const socket = socketService.getSocket();
            if (socket) {
                socket.emit('updateVoiceRoom', updatedRoom);
            }
        }
    };

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    const sendMessage = (text: string) => {
        if (!text.trim() || !selectedRoom) return;
        const message: ChatMessage = {
            id: Date.now().toString(),
            userId: user?.id || 'current-user',
            username: user?.username || 'Sen',
            avatar: user?.profileImage ? getImageUrl(user.profileImage) : (user?.username ? user.username[0].toUpperCase() : '🎭'),
            message: text,
            timestamp: new Date()
        };
        
        setChatMessages(prev => {
            if (prev.some(m => m.id === message.id)) return prev;
            return [...prev, message];
        });

        const socket = socketService.getSocket();
        if (socket) {
            socket.emit('voiceRoomMessage', { roomId: selectedRoom.id, message });
        }
    };

    const sendGift = (gift: Gift, recipientId?: string) => {
        if (userBalance >= gift.price) {
            setUserBalance(prev => prev - gift.price);

            const giftMessage: ChatMessage = {
                id: Date.now().toString(),
                userId: 'system',
                username: 'Sistem',
                avatar: '🎁',
                message: `${gift.icon} ${gift.name} gönderdi!`,
                timestamp: new Date(),
                isGift: true,
                gift: { ...gift, sender: 'Sen', timestamp: new Date() }
            };
            const socket = socketService.getSocket();
            if (socket && selectedRoom) {
                socket.emit('voiceRoomMessage', { roomId: selectedRoom.id, message: giftMessage });
            }
            setChatMessages(prev => [...prev, giftMessage]);
        }
    };

    const removeSpeaker = (participantId: string) => {
        if (!selectedRoom) return;
        const currentUserId = user?.id || (user as any)?._id || 'current-user';
        const isHost = selectedRoom.host === (user?.username || 'Sen');
        
        if (isHost && participantId !== currentUserId) {
            const socket = socketService.getSocket();
            if (socket) {
                const updatedRoom = {
                    ...selectedRoom,
                    participants: selectedRoom.participants.filter(p => p.id !== participantId)
                };
                setSelectedRoom(updatedRoom);
                setRooms(prev => prev.map(r => r.id === updatedRoom.id ? updatedRoom : r));
                socket.emit('updateVoiceRoom', updatedRoom);
            }
        }
    };

    // Video Functions
    const startVideo = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setVideoStream(stream);
            setIsVideoEnabled(true);
            webRTCService.setLocalStream(stream);
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (e) {
            console.error(e);
            alert("Kamera hatası");
        }
    };

    const stopVideo = () => {
        if (videoStream) {
            videoStream.getTracks().forEach(t => t.stop());
            setVideoStream(null);
            setIsVideoEnabled(false);
        }
    };

    return (
        <VoiceChatContext.Provider value={{
            rooms,
            selectedRoom,
            isInRoom,
            isMuted,
            isSpeaking,
            volume,
            chatMessages,
            userBalance,
            isMinimized,
            joinRoom,
            leaveRoom,
            createRoom,
            requestToSpeak,
            acceptSpeakerRequest,
            rejectSpeakerRequest,
            toggleMute,
            toggleMinimize,
            sendMessage,
            sendGift,
            removeSpeaker,
            setIsChatVisible,
            isChatVisible,
            isVideoEnabled,
            videoStream,
            startVideo,
            stopVideo,
            videoRef
        }}>
            {children}
        </VoiceChatContext.Provider>
    );
};

export const useVoiceChat = () => {
    const context = useContext(VoiceChatContext);
    if (context === undefined) {
        throw new Error('useVoiceChat must be used within a VoiceChatProvider');
    }
    return context;
};
